import { Link } from 'react-router'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { parseError } from '@/lib/errors'
import { formatDate } from '@/lib/formatters'
import {
  PolicyChangeAction,
  type PolicyChangeRequest,
  PolicyChangeStatus,
  policyChangeWrites,
  usePolicyChangeMutation,
} from '@/models/policyChange'
import { useCurrentUser } from '@/models/user'
import { AskHistory } from './AskHistory'
import { ChangeLineItem } from './ChangeLineItem'
import { PolicyChangeAskCard } from './PolicyChangeAskCard'
import { KIND_LABELS } from './policyChangeCopy'
import { PolicyChangeProgress } from './PolicyChangeProgress'
import { PolicyChangeQuoteCard } from './PolicyChangeQuoteCard'
import { PolicyChangeStatusBadge } from './PolicyChangeStatusBadge'
import { WithdrawRequest } from './WithdrawRequest'

const CLOSED_UNCHANGED = [PolicyChangeStatus.DECLINED, PolicyChangeStatus.CANCELED]

const ACCEPTED_STATUSES = [
  PolicyChangeStatus.ACCEPTED,
  PolicyChangeStatus.BOUND,
  PolicyChangeStatus.POLICY_UPDATED,
  PolicyChangeStatus.COMPLETE,
]

/** Where a sent request stands, and the customer's decision once it is quoted. */
export function PolicyChangeDetail({ request }: { request: PolicyChangeRequest }) {
  const { user } = useCurrentUser({ enabled: true })
  const orgId = user?.currentOrg.id
  const accept = usePolicyChangeMutation(orgId, policyChangeWrites.accept)
  const decline = usePolicyChangeMutation(orgId, policyChangeWrites.decline)
  const reopen = usePolicyChangeMutation(orgId, policyChangeWrites.reopen)
  const onError = (error: unknown) => toast.error(parseError(error))
  const droppedCount = request.changes.filter((line) => line.dropped).length
  const answered = request.asks.filter((ask) => ask.answeredAt)

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col lg:flex-row lg:items-start lg:gap-8">
      <aside className="px-4 pt-6 lg:w-80 lg:shrink-0 lg:px-0 lg:py-6">
        <div className="mb-1 font-mono text-xs uppercase">Policy change {request.idShort}</div>
        <h1 className="mb-6 text-xl font-bold tracking-[-0.01em]">Where your request stands</h1>
        <PolicyChangeProgress request={request} />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col gap-4 px-4 py-6 lg:px-0">
        <div>
          <PolicyChangeStatusBadge
            status={request.status}
            submitted={!!request.submittedAt}
            waitingOnCustomer={!!request.openAsk}
          />
        </div>
        {request.openAsk && (
          <PolicyChangeAskCard
            key={request.openAsk.id}
            orgId={orgId}
            request={request}
            ask={request.openAsk}
          />
        )}
        {request.actions.includes(PolicyChangeAction.ACCEPT) ? (
          <PolicyChangeQuoteCard
            request={request}
            decision={{
              accepting: accept.isPending,
              onAccept: () =>
                accept.mutate(
                  { requestId: request.id, quotesPostedAt: request.quotesPostedAt ?? '' },
                  { onError }
                ),
              onDecline: () =>
                decline.mutateAsync(request.id).then(
                  () => undefined,
                  (error: unknown) => {
                    onError(error)
                  }
                ),
            }}
          />
        ) : (
          ACCEPTED_STATUSES.includes(request.status) &&
          request.quotes.length > 0 &&
          (request.priceUnchanged ? (
            <Banner variant="info">Your price stays the same with this change.</Banner>
          ) : (
            <PolicyChangeQuoteCard request={request} />
          ))
        )}
        <WithdrawRequest orgId={orgId} request={request} />
        {droppedCount > 0 && !CLOSED_UNCHANGED.includes(request.status) && (
          <Banner variant="warning">
            <span className="font-medium">
              {droppedCount === 1 ? 'One change was' : `${droppedCount} changes were`} dropped from this
              request.
            </span>{' '}
            See why below.
          </Banner>
        )}
        {CLOSED_UNCHANGED.includes(request.status) && (
          <Banner
            variant="info"
            action={
              <div className="flex gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to="/insure/policy-changes/new">Start a new request</Link>
                </Button>
                <Button
                  size="sm"
                  onClick={() => reopen.mutate(request.id, { onError })}
                  loading={reopen.isPending ? 'Reopening…' : undefined}
                >
                  Reopen
                </Button>
              </div>
            }
          >
            {closedMessage(request)}
          </Banner>
        )}
        {request.status === PolicyChangeStatus.COMPLETE && (
          <Banner variant="info">
            Your policies are updated. Your new COI is on the{' '}
            <Link to="/insure/certificates">Certificates</Link> page.
          </Banner>
        )}
        {answered.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Your answers</CardTitle>
            </CardHeader>
            <CardContent>
              <AskHistory asks={answered} answerLabel="You answered" />
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle>Changes</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {request.changes.map((line) => (
              <div key={line.id} className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2 text-sm text-neutral-600">
                  <span className="flex items-center gap-2">
                    {KIND_LABELS[line.kind]}
                    {line.dropped && <Badge variant="warning">Dropped</Badge>}
                  </span>
                  <span>
                    {line.effectiveDate && !line.dropped ? `From ${formatDate(line.effectiveDate)}` : ''}
                  </span>
                </div>
                <ChangeLineItem line={line} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

/** Why nothing changed on the customer's policies. */
function closedMessage(request: PolicyChangeRequest): string {
  if (request.status === PolicyChangeStatus.CANCELED) {
    return request.submittedAt
      ? 'You withdrew this request, so nothing changed on your policies.'
      : 'You canceled this draft, so nothing changed on your policies.'
  }
  if (request.declineReason) return `Your insurance broker declined this request: ${request.declineReason}`
  return 'You declined the quote, so nothing changed on your policies.'
}
